import { Injectable } from '@angular/core';

export type StripeCheckout = {
  redirectToCheckout: (options: { sessionId: string }) => Promise<{ error?: { message?: string } }>;
};

type StripeFactory = (publishableKey: string) => StripeCheckout;

declare global {
  interface Window {
    Stripe?: StripeFactory;
  }
}

@Injectable({ providedIn: 'root' })
export class StripeLoaderService {
  private scriptUrl = 'https://js.stripe.com/v3';
  private stripeCache = new Map<string, Promise<StripeCheckout | null>>();

  load(publishableKey: string): Promise<StripeCheckout | null> {
    if (!publishableKey) {
      return Promise.resolve(null);
    }

    if (this.stripeCache.has(publishableKey)) {
      return this.stripeCache.get(publishableKey)!;
    }

    const loader = this.injectScript()
      .then(() => {
        if (typeof window === 'undefined') {
          return null;
        }

        if (typeof window.Stripe !== 'function') {
          console.error('Stripe.js wurde nicht korrekt geladen.');
          return null;
        }

        try {
          return window.Stripe(publishableKey);
        } catch (error) {
          console.error('Stripe konnte nicht initialisiert werden.', error);
          return null;
        }
      })
      .catch((error) => {
        console.error('Stripe.js konnte nicht geladen werden.', error);
        return null;
      });

    this.stripeCache.set(publishableKey, loader);
    return loader;
  }

  private injectScript(): Promise<void> {
    if (typeof document === 'undefined') {
      return Promise.resolve();
    }

    if (document.querySelector(`script[src="${this.scriptUrl}"]`)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = this.scriptUrl;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = (error) => reject(error);
      document.body.appendChild(script);
    });
  }
}
